import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "./AuthContext";

const KEY = "casaneo_view_mode"; // 'admin' | 'owner'
const ViewRoleContext = createContext(null);

export function ViewRoleProvider({ children }) {
  const { user } = useAuth();

  // Two signals:
  //  * `backendResolved` — the ONLY authoritative source. When the backend
  //    replies to `/local/whoami`, it tells us definitively whether this
  //    session is admin or owner (resolved against `db.owners`). This
  //    survives login-source quirks where the user object misses
  //    `auth_type` or reports the legacy `role="owner"` even for admins.
  //  * `heuristicAdmin` — an optimistic guess based on the raw user
  //    object. Only used as an initial default before `whoami` resolves.
  const apiRole = (user?.role || "").toLowerCase() || null;
  const backendResolved = typeof user?.is_admin_resolved === "boolean"
    ? user.is_admin_resolved
    : null;
  // Signal optimiste, uniquement pour les comptes qui ne peuvent PAS être
  // des propriétaires : clé API super-admin ou rôle Casanéo admin/manager.
  const heuristicAdmin = (
    apiRole === "admin"
    || apiRole === "manager"
    || user?.auth_type === "api_key"
  );
  // FAIL-CLOSED : tant que le backend n'a pas confirmé « admin », un compte
  // au rôle « owner » (propriétaire invité) est enfermé dans son espace.
  // Le backend (`/local/whoami`) reste la seule source qui peut déverrouiller.
  const isLockedOwner = !!user && (backendResolved === false || (backendResolved === null && !heuristicAdmin));
  // Verrou optimiste en attente de la réponse du backend : on affiche un
  // chargement plutôt qu'une redirection (évite d'éjecter un admin legacy).
  const resolving = !!user && backendResolved === null && !heuristicAdmin;
  const isAdmin = backendResolved !== null ? backendResolved : heuristicAdmin;
  const isOwnerAccount = isLockedOwner;
  // Agent ménage : compte verrouillé (vue « owner ») mais espace dédié (À faire / Mon calendrier).
  const isCleaner = !!user && (apiRole === "cleaner" || user?.account_kind === "cleaner");

  const [viewMode, setViewModeState] = useState("admin");
  const manualOwnerPreview = useRef(false);

  // Only force owner view when the backend HAS CONFIRMED the account is
  // owner-only. Never on optimistic heuristics.
  useEffect(() => {
    if (isLockedOwner && viewMode !== "owner") {
      setViewModeState("owner");
    }
    // Déverrouillage : le backend confirme « admin » après un verrou
    // optimiste → on rend la vue gestionnaire (sinon admin « coincé »).
    if (!isLockedOwner && viewMode === "owner" && !manualOwnerPreview.current) {
      setViewModeState("admin");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLockedOwner, backendResolved]);

  // Legacy cleanup — wipe stale localStorage value from the previous
  // persist-on-toggle behaviour.
  useEffect(() => {
    if (typeof window !== "undefined") {
      try { localStorage.removeItem(KEY); } catch { /* noop */ }
    }
  }, []);

  const setViewMode = (m) => {
    // Backend-confirmed owners stay locked. Everyone else — including
    // admins whose user object was momentarily incomplete — can toggle.
    if (isLockedOwner) {
      setViewModeState("owner");
      return;
    }
    manualOwnerPreview.current = m === "owner";
    setViewModeState(m === "owner" ? "owner" : "admin");
  };

  // Autorisations de l'espace propriétaire (réglées par l'agence dans Paramètres).
  const ownerPermissions = {
    statements: true, calendar: true, stats: true,
    ...(isLockedOwner && user?.owner_permissions ? user.owner_permissions : {}),
  };
  const canToggle = !isLockedOwner;
  const effectiveViewMode = isLockedOwner ? "owner" : viewMode;

  const value = useMemo(
    () => ({
      viewMode: effectiveViewMode,
      setViewMode,
      canToggle,
      apiRole,
      isOwnerAccount,
      isCleaner,
      isOwnerView: effectiveViewMode === "owner",
      isAdmin,
      ownerPermissions,
      resolving,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [effectiveViewMode, apiRole, isOwnerAccount, isCleaner, isAdmin, resolving, JSON.stringify(ownerPermissions)]
  );

  return <ViewRoleContext.Provider value={value}>{children}</ViewRoleContext.Provider>;
}

export const useViewRole = () => useContext(ViewRoleContext);
